(function () {
  const sectionSelector = '[data-scroll-to-reveal-section]';
  const storySelector = '[data-scroll-to-reveal-story]';
  const textSelector = '[data-scroll-to-reveal-text]';
  const signatureSelector = '[data-scroll-to-reveal-signature]';
  const signatureLottieSelector = '[data-scroll-to-reveal-signature-lottie]';
  const readyAttribute = 'data-scroll-to-reveal-ready';
  const lottieScriptUrl = 'https://cdnjs.cloudflare.com/ajax/libs/lottie-web/5.12.2/lottie_light.min.js';
  const signatureVisibleProgress = 0.78;
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const sections = new Set();
  let lottieLoading = null;
  let ticking = false;

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function splitText(paragraph, words) {
    const text = paragraph.textContent.trim().replace(/\s+/g, ' ');

    paragraph.textContent = '';
    paragraph.setAttribute('aria-label', text);

    text.split(' ').forEach((word) => {
      const span = document.createElement('span');
      span.className = 'scroll-to-reveal__word';
      span.textContent = word;
      span.setAttribute('aria-hidden', 'true');
      span.style.setProperty('--word-opacity', '0.2');
      paragraph.appendChild(span);
      words.push(span);
    });
  }

  function setWordProgress(words, progress) {
    const revealPosition = progress * words.length;

    words.forEach((word, index) => {
      const distance = revealPosition - index;
      const opacity = clamp(0.2 + distance * 0.8, 0.2, 1);
      word.style.setProperty('--word-opacity', opacity.toFixed(3));
    });
  }

  function setComplete(section) {
    const data = section.scrollToRevealData;
    setWordProgress(data.words, 1);
    updateSignature(section, 1);
  }

  function loadLottie() {
    if (window.lottie) {
      return Promise.resolve(window.lottie);
    }

    if (lottieLoading) {
      return lottieLoading;
    }

    lottieLoading = new Promise((resolve, reject) => {
      const existingScript = document.querySelector(`script[src="${lottieScriptUrl}"], script[src*="lottie_light.min.js"]`);

      function handleLoad() {
        if (window.lottie) {
          resolve(window.lottie);
          return;
        }

        reject(new Error('Lottie failed to load.'));
      }

      if (existingScript) {
        existingScript.addEventListener('load', handleLoad, { once: true });
        existingScript.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = lottieScriptUrl;
      script.defer = true;
      script.dataset.kiaraLottieWeb = 'true';
      script.addEventListener('load', handleLoad, { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });

    return lottieLoading;
  }

  function playSignature(signature) {
    if (!signature || signature.played) {
      return;
    }

    if (signature.animation) {
      signature.played = true;
      signature.animation.goToAndPlay(0, true);
    }
  }

  function mountSignatureAnimation(signature) {
    if (!signature || !signature.container || !signature.url || reducedMotionQuery.matches) {
      return;
    }

    loadLottie()
      .then((lottie) => {
        if (signature.animation) {
          return;
        }

        signature.animation = lottie.loadAnimation({
          container: signature.container,
          renderer: 'svg',
          loop: false,
          autoplay: false,
          path: signature.url
        });
        signature.element.classList.add('is-lottie-ready');

        if (signature.visible) {
          playSignature(signature);
        }
      })
      .catch(() => {});
  }

  function updateSignature(section, progress) {
    const data = section.scrollToRevealData;
    const signature = data && data.signature;

    if (!signature) {
      return;
    }

    signature.visible = reducedMotionQuery.matches || progress >= signatureVisibleProgress;
    section.classList.toggle('is-signature-visible', signature.visible);

    if (signature.visible && !reducedMotionQuery.matches) {
      playSignature(signature);
    }
  }

  function initSignature(section) {
    const element = section.querySelector(signatureSelector);

    if (!element) {
      return null;
    }

    const signature = {
      element,
      container: element.querySelector(signatureLottieSelector),
      url: element.getAttribute('data-signature-url'),
      animation: null,
      played: false,
      visible: false
    };

    mountSignatureAnimation(signature);

    return signature;
  }

  function getProgress(section) {
    const data = section.scrollToRevealData;
    const target = data && data.story ? data.story : section;
    const rect = target.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    const revealStart = viewportHeight * 1.0;
    const revealEnd = viewportHeight * -0.05;
    const progress = (revealStart - rect.top) / (revealStart - revealEnd);

    return clamp(progress, 0, 1);
  }

  function updateSection(section) {
    const data = section.scrollToRevealData;

    if (!data || !data.words.length) {
      return;
    }

    if (reducedMotionQuery.matches) {
      setComplete(section);
      return;
    }

    const progress = getProgress(section);
    setWordProgress(data.words, progress);
    updateSignature(section, progress);
  }

  function updateAllSections() {
    sections.forEach(updateSection);
    ticking = false;
  }

  function requestUpdate() {
    if (ticking) {
      return;
    }

    ticking = true;
    window.requestAnimationFrame(updateAllSections);
  }

  function initSection(section) {
    if (section.hasAttribute(readyAttribute)) {
      const existingWords = Array.from(section.querySelectorAll('.scroll-to-reveal__word'));

      if (existingWords.length) {
        section.scrollToRevealData = {
          story: section.querySelector(storySelector),
          words: existingWords,
          signature: section.scrollToRevealData && section.scrollToRevealData.signature
            ? section.scrollToRevealData.signature
            : initSignature(section)
        };
        sections.add(section);
        updateSection(section);
      }

      return;
    }

    const words = [];
    section.querySelectorAll(textSelector).forEach((paragraph) => splitText(paragraph, words));

    section.scrollToRevealData = {
      story: section.querySelector(storySelector),
      words,
      signature: initSignature(section)
    };

    section.setAttribute(readyAttribute, 'true');
    sections.add(section);
    updateSection(section);
  }

  function init(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(sectionSelector).forEach(initSection);
    requestUpdate();
  }

  window.KiaraScrollToReveal = {
    init,
    requestUpdate,
  };

  document.addEventListener('DOMContentLoaded', function () {
    init(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate);

  if (reducedMotionQuery.addEventListener) {
    reducedMotionQuery.addEventListener('change', requestUpdate);
  }
})();
